import React, { useState } from 'react';
import { Check, Loader2, Target, X } from 'lucide-react';
import { apiService } from '../../services/api';

// Actions proposées par Hermes : relues, corrigées puis transformées en objectifs du projet, ou écartées. Réunion d'un
// client ou d'un programme : chaque action va au projet choisi (celui que Nexus a proposé, modifiable).
const ActionRow = ({ meetingId, action, employees, canEdit, onChange, projects }) => {
  const [title, setTitle] = useState(action.title);
  const [ownerId, setOwnerId] = useState(action.owner_employee_id || '');
  const [dueDate, setDueDate] = useState(action.due_date || '');
  const [projectId, setProjectId] = useState(action.project_id || '');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const decide = async (decision) => {
    setBusy(decision);
    setError(null);
    try {
      onChange(await apiService.post(`/meetings/${meetingId}/actions/${action.id}`, {
        decision,
        ...(decision === 'accept' ? {
          title: title.trim() || action.title,
          ...(ownerId ? { owner_employee_id: Number(ownerId) } : {}),
          ...(dueDate ? { due_date: dueDate } : {}),
          ...(projects && projectId ? { project_id: Number(projectId) } : {}),
        } : {}),
      }));
    } catch (err) {
      setError(err.message);
      setBusy(null);
    }
  };

  if (action.status === 'accepted') {
    return (
      <li className="flex items-start gap-2 rounded-lg border border-emerald-100 bg-emerald-50/50 px-3 py-2 text-sm text-slate-700">
        <Target className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        <span className="flex-1">{action.title}
          <span className="ml-2 text-xs text-emerald-700">
            objectif créé{projects && action.project_name ? ` dans ${action.project_name}` : ''}{action.owner_label ? ` · ${action.owner_label}` : ''}
          </span>
        </span>
      </li>
    );
  }
  if (action.status === 'rejected') {
    return (
      <li className="rounded-lg border border-slate-100 px-3 py-2 text-sm text-slate-400 line-through">{action.title}</li>
    );
  }
  if (!canEdit) {
    return (
      <li className="rounded-lg border border-slate-100 px-3 py-2 text-sm text-slate-700">
        {action.title}
        <span className="ml-2 text-xs text-slate-400">
          {[projects && action.project_name, action.owner_label, action.due_date].filter(Boolean).join(' · ')}
        </span>
      </li>
    );
  }
  return (
    <li className="rounded-lg border border-slate-200 p-2.5">
      <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300}
        className="w-full rounded-md px-1.5 py-1 text-sm text-slate-800 outline-none focus:bg-slate-50" />
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        {projects && (
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} title="Projet de l'objectif"
            className={`max-w-[14rem] rounded-md border px-2 py-1 text-xs ${projectId ? 'border-slate-200 text-slate-600' : 'border-amber-300 text-amber-800'}`}>
            <option value="">Projet à choisir</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}
          className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600">
          <option value="">{action.owner_label ? `« ${action.owner_label} » (à préciser)` : 'Sans responsable'}</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
          className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600" />
        <div className="ml-auto flex gap-1.5">
          <button type="button" onClick={() => decide('reject')} disabled={Boolean(busy)}
            className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:bg-slate-50">
            {busy === 'reject' ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />} Écarter
          </button>
          <button type="button" onClick={() => decide('accept')} disabled={Boolean(busy) || (projects && !projectId)}
            title={projects && !projectId ? "Choisis d'abord le projet de l'objectif" : undefined}
            className="flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
            {busy === 'accept' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Créer l'objectif
          </button>
        </div>
      </div>
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
    </li>
  );
};

const MeetingActions = ({ meeting, employees, onChange }) => {
  if (!meeting.actions?.length) return null;
  const pending = meeting.actions.filter((a) => a.status === 'proposed').length;
  // Client ou programme : les projets concernés d'abord, puis ses autres projets.
  const several = meeting.scope && meeting.scope.kind !== 'project';
  const projects = several ? [
    ...(meeting.projects || []).map((p) => ({ id: p.project_id, name: p.name })),
    ...(meeting.scope.projects || []).filter((p) => !(meeting.projects || []).some((c) => c.project_id === p.id)),
  ] : null;
  return (
    <div>
      <h3 className="mb-1.5 text-sm font-semibold text-slate-900">
        Actions proposées
        {pending > 0 && meeting.can_edit && <span className="ml-2 text-xs font-normal text-slate-400">{pending} à valider</span>}
      </h3>
      <ul className="space-y-1.5">
        {meeting.actions.map((action) => (
          <ActionRow key={`${action.id}-${action.status}-${action.project_id || ''}`} meetingId={meeting.id} action={action}
            employees={employees} canEdit={meeting.can_edit} onChange={onChange} projects={projects} />
        ))}
      </ul>
    </div>
  );
};

export default MeetingActions;
